import type { CoachCard } from "@pushback-and-chaos/contracts";

/**
 * THE CONNECTION BETWEEN A RECOMMENDATION AND ITS FILE (2026-10-31, Andre,
 * from the arrange-files ideation, chunks 10 to 13). A card AJ pushed carries
 * the id of the reply whose words put it on the desk (CoachCard.messageId;
 * coach_pushes.message_id, migration 0050). From that one fact the desk draws
 * the relationship both ways: a marker at the end of the paragraph in the
 * conversation that names the case, a marker on the file's own reason line,
 * and, while one of them is selected, a line between the two.
 *
 * The pure parts live here, so they can be tested without a desk: which
 * cards are linked, which paragraph of a reply is the recommendation, and
 * where a line between two rectangles starts and ends. Nothing here stores a
 * paragraph position: the recommendation paragraph is found again from the
 * reply's text each time, so re-wrapped prose can never point at the wrong
 * place, and a link whose reply or card is missing draws nothing at all.
 */
export interface LinkedFile {
  /** The desk's own key for the card (`cardKey`), the paper's `data-key`. */
  readonly key: string;
  /** The id of the coach's reply that made the push: `CoachThreadMessage.id`. */
  readonly messageId: string;
  readonly title: string;
  /** The push was made for the focus that is chosen now. */
  readonly forFocus: boolean;
  /** The push was made for a focus that is no longer the chosen one: it supports an earlier focus, and says so. */
  readonly forEarlierFocus: boolean;
}

/**
 * The cards on the desk that carry a link, by the id of the reply that made
 * them. Only a PUSHED card with a recorded `messageId` counts; a card with
 * neither link is an ordinary card and is simply absent here. `focusId` is
 * the chosen focus's id, or null when none is chosen.
 */
export function linkedFiles(cards: readonly CoachCard[], keyOf: (card: CoachCard, index: number) => string, focusId: string | null): ReadonlyMap<string, LinkedFile> {
  const files = new Map<string, LinkedFile>();
  cards.forEach((card, index) => {
    if (card.kind !== "PUSHED" || card.messageId === undefined) return;
    const forFocus = card.focusId !== undefined && focusId !== null && card.focusId === focusId;
    files.set(card.messageId, { key: keyOf(card, index), messageId: card.messageId, title: card.title, forFocus, forEarlierFocus: card.focusId !== undefined && !forFocus });
  });
  return files;
}

/**
 * Which paragraph of a reply is the recommendation: the first that names the
 * case (its whole title, else its first word when that word is long enough
 * to be a name, so "Drayvale" finds "Drayvale Transit" said shortly), and
 * the first paragraph when none does. Case-insensitive; never out of range.
 */
export function recommendationParagraph(paragraphs: readonly string[], title: string): number {
  if (paragraphs.length === 0) return 0;
  const lower = paragraphs.map((p) => p.toLowerCase());
  const whole = title.trim().toLowerCase();
  const byWhole = whole.length > 0 ? lower.findIndex((p) => p.includes(whole)) : -1;
  if (byWhole >= 0) return byWhole;
  const first = whole.split(/\s+/)[0] ?? "";
  if (first.length >= 4) {
    const byFirst = lower.findIndex((p) => p.includes(first));
    if (byFirst >= 0) return byFirst;
  }
  return 0;
}

export interface LinkRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface LinkPoint {
  readonly x: number;
  readonly y: number;
}

/** A rectangle is visible when any of it lies within the viewport. */
export function visibleIn(rect: LinkRect, viewport: { readonly width: number; readonly height: number }): boolean {
  return rect.right > 0 && rect.bottom > 0 && rect.left < viewport.width && rect.top < viewport.height && rect.right > rect.left && rect.bottom > rect.top;
}

/**
 * Where a line between two rectangles starts and ends: from the facing
 * vertical edge of each when they are side by side (the conversation on the
 * left, the desk on the right), from the facing horizontal edge when one is
 * above the other (the narrow layout). Each point sits on the edge's middle.
 * Null when the two overlap, which on the desk means a card lying on or
 * under the focus paper: no line is drawn, the lying together says it.
 */
export function linkEnds(a: LinkRect, b: LinkRect): { readonly from: LinkPoint; readonly to: LinkPoint } | null {
  const midY = (r: LinkRect): number => (r.top + r.bottom) / 2;
  const midX = (r: LinkRect): number => (r.left + r.right) / 2;
  if (a.right <= b.left) return { from: { x: a.right, y: midY(a) }, to: { x: b.left, y: midY(b) } };
  if (b.right <= a.left) return { from: { x: a.left, y: midY(a) }, to: { x: b.right, y: midY(b) } };
  if (a.bottom <= b.top) return { from: { x: midX(a), y: a.bottom }, to: { x: midX(b), y: b.top } };
  if (b.bottom <= a.top) return { from: { x: midX(a), y: a.top }, to: { x: midX(b), y: b.bottom } };
  // The two lie on each other (a card on, or under, the focus paper): their lying together is the connection; no line.
  return null;
}

/**
 * The line itself, as an SVG path: a gentle cubic between the two ends, its
 * handles pulled along the axis the ends are apart on, so a line across the
 * page leaves and arrives level and a line down the page leaves and arrives
 * upright. Coordinates are rounded to the half pixel so the path string is
 * stable from frame to frame when nothing has moved.
 */
export function linkPath(from: LinkPoint, to: LinkPoint): string {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const across = Math.abs(dx) >= Math.abs(dy);
  const pull = Math.max(24, (across ? Math.abs(dx) : Math.abs(dy)) * 0.4);
  const c1 = across ? { x: from.x + Math.sign(dx || 1) * pull, y: from.y } : { x: from.x, y: from.y + Math.sign(dy || 1) * pull };
  const c2 = across ? { x: to.x - Math.sign(dx || 1) * pull, y: to.y } : { x: to.x, y: to.y - Math.sign(dy || 1) * pull };
  const r = (n: number): string => String(Math.round(n * 2) / 2);
  return `M ${r(from.x)} ${r(from.y)} C ${r(c1.x)} ${r(c1.y)}, ${r(c2.x)} ${r(c2.y)}, ${r(to.x)} ${r(to.y)}`;
}
